import React from "react";
import { usePanelNavigation } from "./vmSlidingPanel";
import { VMAuthPanel } from "./vmAuthPanel";
import "../votemachine.scss";

import image_manual from '../images/manual.jpg';
import image_auth_panel from '../images/auth_panel.jpg';
import image_questionnaire from '../images/questionnaire.jpg';
import image_homepage from '../images/homepage.jpg';
import image_discover_assemblies from '../images/discover_assemblies.jpg';
import image_proposals from '../images/proposals.jpg';
import image_vote_proposal from '../images/vote_proposal.jpg';
import image_reports from '../images/reports.jpg';
import image_fine from '../images/fine.jpg';
import image_magic_link from '../images/magic_link_email.jpg';

export function VMManual({changeSection}) {
  const { pushPanel } = usePanelNavigation();

  return (
    <div className="vm-section">
      <div className="vm-section-title">
        <h2>Manual</h2>
      </div>
      <div className="vm-section-sub-section">
        <p><b>Vote Machine</b> is a delegated polling system based on the concepts of &nbsp;
          <a href="https://participedia.net/method/liquid-democracy">Liquid Democracy</a>.
        </p>
      </div>

      <div className="vm-section-sub-header">
        <button
          style={{ width: '50%', fontWeight: 'bold', fontSize: '20px', padding: '10px', marginBottom: '20px' }}
          onClick={() => pushPanel(<VMAuthPanel changeSection={changeSection}/>)}
        >
          Sign In or Sign Up
        </button>
      </div>
      <div className="vm-section-sub-header">
        <button
          style={{ width: '50%', fontWeight: 'bold', fontSize: '18px', padding: '10px' }}
          onClick={() => pushPanel(<VMHowStartPanel changeSection={changeSection}/>)}
        >
          How Do I Get Started?
        </button>
      </div>
      <div className="vm-section-sub-header">
        <button
          style={{ width: '50%', fontWeight: 'bold', fontSize: '18px', padding: '10px' }}
          onClick={() => pushPanel(<VMHowWorksPanel changeSection={changeSection}/>)}
        >
          How Does It Work?
        </button>
      </div>
      <div className="vm-section-sub-header">
        <button
          style={{ width: '50%', fontWeight: 'bold', fontSize: '18px', padding: '10px' }}
          onClick={() => pushPanel(<VMWhyMatterPanel changeSection={changeSection}/>)}
        >
          Why Does It Matter?
        </button>
      </div>
      <div className="vm-section-sub-header">
        <button
          style={{ width: '50%', fontWeight: 'bold', fontSize: '18px', padding: '10px' }}
          onClick={() => pushPanel(<VMWhatPlanPanel changeSection={changeSection}/>)}
        >
          What Is The Plan?
        </button>
      </div>

      <p>&nbsp;</p>

      <div className="vm-section-sub-section">
        <b>Glossary of Terms</b>
        <ul>
          <li><strong>Assemblies:</strong> Groups of voters who can vote on sets of proposals.</li>
          <li><strong>Voters:</strong> Users of Vote Machine, empowered to join assemblies, cast votes, and create arguments.</li>
          <li><strong>Proposals:</strong> Ideas put forward to be voted upon. Can be action items or abstract notions.</li>
          <li><strong>Arguments:</strong> Persuasive comments for or against proposals, sorted by an internal ranking system.</li>
          <li><strong>Sources:</strong> Websites or documents providing support for arguments.</li>
          <li><strong>Votes:</strong> Indication of support or opposition to proposals, arguments, or sources.</li>
          <li><strong>Delegates:</strong> Voters that are empowered by other voters to vote for them, by topic and assembly.</li>
        </ul>
        <div className="vm-quoted-text" >
          <p>Vote Machine is dedicated to the memory of Leif Brecke, 1978 - 2016</p>
        </div>
      </div>

      <div className="vm-section-sub-header">
        <button
          className="px-4 py-2 bg-gray-400 text-black font-medium rounded border"
          onClick={() => pushPanel(<VMRulesPanel changeSection={changeSection}/>)}
        >
          Vote Machine Rules of Engagement
        </button>
      </div>

      <div className="vm-quoted-text-wide-bottom">
        <hr></hr>
        "We hold these truths to be self-evident, that all men are created equal, that they are endowed by their Creator with certain unalienable Rights,
         that among these are Life, Liberty and the pursuit of Happiness, ... That to secure these rights, Governments are instituted among
         Men, deriving their just powers from the consent of the governed, ... That whenever any Form of Government becomes destructive of these ends,
         it is the Right of the People to alter or to abolish it, and to
         institute new Government. - Declaration of Independence, 1776"
      </div>
    </div>
  );
}


/////////////////////////////////////////////////////////////

const VMHowStartPanel = ({ changeSection }) => {
  return (
    <div className="vm-section">
      <div className="vm-section-sub-header">
        <b>Getting Started with Vote Machine</b>
      </div>

      <div className="vm-section-sub-section">
        <p>Hello! The first thing to know is that the app in front of you is a DEMO! By all means, please play around with it,
          but let's try not to take it too seriously yet. Major changes could happen, content may even be lost, and a lot of
          bug fixes and feature expansions will surely be done, before we stabilize into a final production version.
          We do hope, however, that this prototype strikes a nerve and proves useful.
        </p>

        <div className="vm-manual-content">
          <div className="vm-manual-content-section">
            <div className="vm-manual-text-column">
              <h3>Getting Started</h3>
              <p>Begin by accessing the manual page and hitting the "Sign In or Sign Up" button at the top.</p>
            </div>
            <div className="vm-manual-image-column">
              <img src={image_manual} alt="Vote Machine Manual page with sign in or sign up button" />
            </div>
          </div>

          <div className="vm-manual-content-section">
            <div className="vm-manual-text-column">
              <h3>Authentication</h3>
              <p>A working email is required to use Vote Machine, as a security measure against spammers and bots, but your email will never be
                shared with other users or anyone else, and it is hashed in the database so that developers and admins won't accidentally see it either.
                Use the sign-in panel to create a new account or log in to an existing one, either by password or email link.</p>
            </div>
            <div className="vm-manual-image-column">
              <img src={image_auth_panel} alt="The sign in panel" />
            </div>
          </div>

          <div className="vm-manual-content-section">
            <p>Click on this link in the authentication email.</p>
            <img src={image_magic_link} style={{ width: '80%'}} alt="Sign in email with link" />
          </div>

          <div className="vm-manual-content-section">
            <div className="vm-manual-text-column">
              <h3>Profile Questions</h3>
              <p>A huge part of the mission of Vote Machine is to discover and highlight issues where most people agree, despite their
              differences on other topics. This questionnaire allows us to show results of votes based on personal politics, age, gender, etc. in order
              to fulfill this mission. None of this information will ever be released or shared except as aggregate results for queries.</p>
            </div>
            <div className="vm-manual-image-column">
              <img src={image_questionnaire} alt="Voter Profile Questionnaire to asses demographic information (not shared)." />
            </div>
          </div>

          <div className="vm-manual-content-section">
            <div className="vm-manual-text-column">
              <h3>Home Page</h3>
              <p>Once logged in, you'll see your homepage displaying all the assemblies you've joined, starting with the two default assemblies
                that everyone belongs to: General Public and Vote Machine. The second is for feedback and discussion of the app itself.</p>
            </div>
            <div className="vm-manual-image-column">
              <img src={image_homepage} alt="Voter Homepage with list of assemblies you have joined" />
            </div>
          </div>

          <div className="vm-manual-content-section">
            <div className="vm-manual-text-column">
              <h3>Discover Assemblies</h3>
              <p>Browse and join new assemblies through the discover assemblies menu. Assemblies can be public or private, and the only ones
                visible to you will be (A) public ones and (B) private ones to which you have been invited.</p>
            </div>
            <div className="vm-manual-image-column">
              <img src={image_discover_assemblies} alt="Discover assemblies menu, for joining assemblies" />
            </div>
          </div>

          <div className="vm-manual-content-section">
            <div className="vm-manual-text-column">
              <h3>Assembly Proposals</h3>
              <p>View all of an assembly's proposals on the proposal list panel. Clicking on them takes you to the voting panel.
                Click on a category to filter the list, and toggle the New/Old sort button to change the order.
              </p>
            </div>
            <div className="vm-manual-image-column">
              <img src={image_proposals} alt="Proposals list panel for an assembly" />
            </div>
          </div>

          <div className="vm-manual-content-section">
            <div className="vm-manual-text-column">
              <h3>Voting Interface</h3>
              <p>View proposal details, cast your vote, and read and contribute arguments in the proposal voting panel.</p>
            </div>
            <div className="vm-manual-image-column">
              <img src={image_vote_proposal} alt="Proposal voting panel" />
            </div>
          </div>

          <div className="vm-manual-content-section">
            <div className="vm-manual-text-column">
              <h3>Reports</h3>
              <p>Access voting statistics and assembly information through the reports panel. Use the demographic filters to show results for specific groups of voters.
                Bar graphs and pie charts are the first two available graph types.</p>
            </div>
            <div className="vm-manual-image-column">
              <img src={image_reports} alt="Reports panel" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


/////////////////////////////////////////////////////////////

const VMHowWorksPanel = ({ changeSection }) => {
  return (
    <div className="vm-section">
      <div className="vm-section-sub-header">
        <b>How Vote Machine Works</b>
      </div>

      <div className="vm-section-sub-section">
        Vote Machine is built on the following core principles:
        <ul>
          <li><b>Anonymity</b> The only thing Vote Machine knows about you is your email, and that is never shared with other users.
              You are publicly represented only by a UUID (Universally Unique Identifier) and this will be all any other user ever knows
              about you, unless you take it upon yourself to share more, which is discouraged. Vote Machine is about the statements people
              make and the votes they cast, not their names or personal identities. </li>
          <li><b>Delegation</b> It is not at all required, but one of the core concepts of Vote Machine is &nbsp;
              <a href="https://participedia.net/method/liquid-democracy">Liquid Democracy</a>, which refers to a fluid system of delegated voting,
              allowing participants to find others more knowledgeable about certain subjects but generally aligned in sentiment, and to delegate their votes in those subjects to these other users.
              This is to allow your voice to be heard without having to spend all of your time voting on things that you may not be an expert in.</li>
          <li><b>Demographic Feedback</b> A core founding idea of Vote Machine is the assertion that people are less divided than is generally assumed, once you
              get past the most polarizing "hot button" issues and start talking about specific, day to day, local concerns. The mechanism by which we hope to demonstrate this is the demographic survey
              which all users are required to complete before they are allowed to join assemblies or vote on proposals. None of this data will ever be shared or
              made available in any way except in the aggregate, for reports.</li>
              <li><b>Community Moderation</b> All aspects of Vote Machine are driven by votes. Arguments and sources are sorted and rated by user votes, with the intent
              that the best and most interesting will rise to the top and the less so will stay below. Voters are only allowed to make arguments on a proposal after they
              have chosen a side, and they are only allowed to submit arguments and vote on arguments on their side of the debate, to prevent malicious down voting
              of good arguments for the opposing side.</li>
        </ul>
        Vote Machine is built on a technology stack which includes React and Three JS for the front end, and an AWS lambda function accessing a dynamoDB database on the back end.
        We use Google's Firebase for authentication, and will also be using Google's Safe Browsing API to protect users from malicious links.
        <p>&nbsp;</p>
      </div>
    </div>
  );
};

/////////////////////////////////////////////////////////////

const VMWhyMatterPanel = ({ changeSection }) => {
  const { pushPanel } = usePanelNavigation();
  return (
    <div className="vm-section">
      <div className="vm-section-sub-header">
        <b>Democracy Is On Thin Ice</b>
      </div>
      <div style={{ textAlign: 'center'}}><img src={image_fine} style={{ width: '30%' }}alt="This Is Fine meme with dog sitting in burning room." /></div>
      <div className="vm-section-sub-section">
        At least in the United States, there were some very troubling political trends afoot, even before the chaos introduced by the 47th President:
        <ul>
          <li>Extreme political polarization among ordinary people, exacerbated if not explicitly manufactured by social media algorithms and corporate media.</li>
          <li>Misinformation and disinformation abounds, while disagreement over basic sources of truth make productive discussions impossible.</li>
          <li>Trust in the institutions of our government is cratering, a sentiment reinforced frequently by shocking examples of unabashed corruption by elected officials and judges.</li>
          <li>Previously unseen levels of wealth inequality, accompanied by drastically rising costs of housing, health care and basic necessities.</li>
          <li>Ever accelerating environmental and climate emergencies: record wildfires, droughts, and heat waves in some areas, excessive rainfall or record cold in others,
            extreme wind events, sea level rise.</li>
          <li>An increasingly fraught global political climate with multiple hot conflicts threatening to expand into regional if not global war.</li>
        </ul>
        <p>In the face of all this, Vote Machine is an attempt to back away from the prevailing insanity and foster new discussions, based on provable facts and sound logic.
        It is built around the principle of community moderation, with the best ideas rising to the top. No idea is off limits*.</p>
        <p>This is a place to discuss solutions that are physically and scientifically possible, not just those that are "politically feasible".</p>
        <p>
          <i>
            * Barring those subjects prohibited under Vote Machine's &nbsp;
            <button
              className="px-4 py-2 bg-gray-400 text-black font-medium rounded border"
              onClick={() => pushPanel(<VMRulesPanel changeSection={changeSection}/>)}
            >
              Rules of Engagement
            </button>.
          </i>
        </p>
        <p>&nbsp;</p>
      </div>
    </div>
  );
};

/////////////////////////////////////////////////////////////

const VMWhatPlanPanel = ({ changeSection }) => {
  const { pushPanel } = usePanelNavigation();
  return (
    <div className="vm-section">
      <div className="vm-section-sub-header">
        <b>What Is The Plan?</b>
      </div>
      <div className="vm-section-sub-section">
        <p>At the time of this writing, Vote Machine is in its infancy, and what can be accomplished will be determined by whether or not it strikes a nerve and becomes a thing.</p>
        <p>It is urgently hoped, however, that the infrastructure here can help facilitate actions in addition to ideas, and can help
        coordinate efforts of local communities to channel their time and resources in productive ways.</p>
        <p>Keep an eye on this space for future developments as they manifest.</p>
      </div>
    </div>
  );
};

/////////////////////////////////////////////////////////////

const VMRulesPanel = ({ changeSection }) => {
  return (
    <div className="vm-section">
      <div className="vm-section-sub-header">
        <b>Rules of Engagement</b>
      </div>
      <div className="vm-section-sub-section">
        <p>Vote Machine is a place for friendly discussion and debate, accessible to all ages and all people,
          and we want it to be a place where everybody feels safe to share their honest opinions. We take this
          responsibility seriously, and will not hesitate to ban accounts that consistently degrade the experience
          of other users. Examples of unacceptable behavior include:</p>
        <ul>
          <li>Personal attacks</li>
          <li>Threats of violence</li>
          <li>Spamming</li>
          <li>Malicious links</li>
          <li>Proposals targeting any group of people based on ethnicity, language, culture, sexual identity or any other factor not subject to individual control.</li>
        </ul>
        In general, please just be polite and respectful, and engage with other users in an adult manner.
        We are all here to make the world a better place.
        <p></p>
      </div>
    </div>
  );
};

//export { VMManual };
